import { Accordion, AccordionDetails, AccordionSummary, Button, Container, Divider, FormControl, FormControlLabel, FormGroup, Switch, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { ForecastSetting } from "./ForecastSetting";

import ResetIcon from '@mui/icons-material/SettingsBackupRestore'; 
import { useDispatch, useSelector } from "react-redux";
import { getWaterfloodForecast, getParams, saveParams, selectWells, getForecast, updateWellStatus } from "../reducers/wellSlice";
import { selectOptions, setUnits, toggleAssistedForecast } from "../reducers/optionsSlice";
import { formatFluid } from "../helpers/formatFluid";
import { Add, ExpandMore, Info, Remove, Storage, Sync } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { getTypeCurveForecast, getTypeCurveParams, saveTypeCurveParams, selectTypeCurves } from "../reducers/typeCurveSlice";
import { selectLists } from "../reducers/listSlice";
import { selectUser } from "../reducers/userSlice";
import { getGroupWellForecast, getGroupWellParams, getWaterfloodForecastGW, saveGroupWellParams, selectGroupWells } from "../reducers/groupWellSlice";
import { WaterfloodSetting } from "./WaterfloodSetting";
import { useState } from "react";
import { selectProjects } from "../reducers/projectSlice";
import { useSocket } from "../context/useSocket";

const useStyles = makeStyles(theme => ({
  root: {
    // backgroundColor: 'green',
    // backgroundColor: theme.palette.grey[50],
    backgroundColor: 'transparent',
    width: '100%',
    height: 'calc(100vh)',
    overflowY: 'auto',
    padding: 0,
    paddingBottom: 140
  },
  heading: {
    // marginBottom: 20,
    fontSize: 18
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0 10px',
    marginBottom: 0,
  },
  toggleButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 10
  },
  cutoff: {
    padding: '0 10px'
  },
  addSegmentButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    margin: '10px 0'
  },
  segment: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'transparent',
  },
  emptyForecast: {
    height: 'calc(100% - 120px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  details: {
    margin: 0,
    padding: 0
  }
}));

export const ForecastSettings = () => {

  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const wells = useSelector(selectWells);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId?.[projects?.currentProjectId];
  const lists = useSelector(selectLists);
  const list = lists?.byId?.[lists?.currentListId];
  const typeCurves = useSelector(selectTypeCurves);
  const typeCurve = typeCurves?.byId[list?.typeCurveId];
  const groupWells = useSelector(selectGroupWells);
  const groupWell = groupWells?.byId[list?.groupWellId];
  const options = useSelector(selectOptions);

  const darkMode = user?.value?.darkMode;
  const demoUser = user?.value?.subscription === 'Demo';
  
  const well = list?.forecastListTab === 0 ? wells?.byUwi?.[wells?.currentWellUwi] : 
    list?.forecastListTab === 1 ? typeCurves?.byId?.[list?.typeCurveId] : groupWells?.byId?.[list?.groupWellId];

  const { enqueueSnackbar } = useSnackbar();

  const [lockedParams, setLockedParams] = useState([]);

  const { socket } = useSocket();


  return (
    <Container className={classes.root} style={{color: "primary"}}>
      <div style={{margin: 10}}>
        <Typography variant="subtitle2" color="textSecondary">
          {list?.forecastListTab === 0 ? well?.formattedUwi : well?.name}
        </Typography>
        <Typography variant="h6" className={classes.heading}>
          {`${formatFluid(options.fluid)} Forecast Settings`}
        </Typography>
      </div>
    
      {(well?.fullCycle && list?.forecastListTab === 2) ? 
      <FormControl component="fieldset" style={{width: '100%', padding: '0 10px', marginTop: 10}}>
        <Typography>Units</Typography>
        <ToggleButtonGroup
          value={options?.units || "metric"}
          exclusive
          onChange={handleUnitsChange}
          size="small"
          style={{height: 28, width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 20}}
        >
          <ToggleButton value="metric" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
            <Typography style={{fontSize: 12}}>
              Metric
            </Typography>
          </ToggleButton>
          <ToggleButton value="imperial" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
            <Typography style={{fontSize: 12}}>
              Field
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      :
      (!well?.copy && well?.params?.[options?.fluid] && Object.keys(well?.params?.[options?.fluid])?.length > 0) ?
      <>
        <div className={classes.buttons}>
          {list?.forecastListTab === 0 && !well?.params?.oil?.useWaterflood &&
          <Tooltip title="Re-fit forecast parameters (shortcut F9)">
            <span><Button 
              variant="contained" 
              color={(darkMode ? "inherit" : "primary")}
              style={{color: (darkMode ? '#000' : '#fff'), height: 28}}
              startIcon={<Sync />} 
              size='small' 
              onClick={() => handleRefitClick(well, list.forecastListTab)}
              disabled={demoUser}
            >
              Re-fit
            </Button></span>
          </Tooltip>}

          <Tooltip title='Revert forecast parameters to the last saved version'>
            <Button 
              variant="outlined" 
              color={(darkMode ? "inherit" : "primary")}
              style={{color: (darkMode ? '#fff' : theme.palette.primary.main), borderColor: (darkMode ? '#fff' : theme.palette.primary.main), height: 28}}
              startIcon={<ResetIcon />} 
              size='small' 
              onClick={() => handleRevertClick(well, list.forecastListTab)}
            >
              Revert
            </Button>
          </Tooltip>

          <Tooltip title='Save forecast parameters to the central database'>
            <span><Button 
              variant="outlined" 
              color={(darkMode ? "inherit" : "primary")}
              // style={{color: (darkMode ? '#fff' : theme.palette.primary.main), borderColor: (darkMode ? '#fff' : theme.palette.primary.main)}}
              startIcon={<Storage />} 
              size='small' 
              onClick={() => well && handleSaveClick(well, list.forecastListTab)}
              disabled={(
                !user?.value?.email?.includes('ssdynamics.com') && 
                !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL) && 
                !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL2) &&
                !user?.value?.email?.includes(process.env.REACT_APP_CDGR_ADMIN_EMAIL3) 
              ) || demoUser}
              style={{height: 28}}
            >
              Save
            </Button></span>
          </Tooltip>
        </div>

        <FormControl component="fieldset" style={{width: '100%', padding: '0 10px', marginTop: 20}}>
          <Typography>Forecast Type</Typography>
          <ToggleButtonGroup
            value={"analytical"}
            exclusive
            onChange={() => {}}
            size="small"
            style={{height: 28, width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 0}}
          >
            <ToggleButton value="analytical" size="small" style={{width: '33.3%'}} color={(darkMode ? "" : "primary")}>
              <Typography style={{fontSize: 12}}>
                Analytical
              </Typography>
            </ToggleButton>
            <ToggleButton value="ai" size="small" style={{width: '33.3%'}} color={(darkMode ? "" : "primary")} disabled>
              <Typography style={{fontSize: 12}}>
                AI
              </Typography>
            </ToggleButton>
            <ToggleButton value="physics" size="small" style={{width: '33.3%'}} color={(darkMode ? "" : "primary")} disabled>
              <Typography style={{fontSize: 12}}>
                Physics
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        {options.fluid === 'oil' &&
        <div style={{display: 'flex', alignItems: 'center'}}>
          <FormGroup style={{marginTop: 5, marginLeft: 10}}>
            <FormControlLabel 
              control={<Switch />} 
              label={<Typography style={{fontSize: 14}}>Use Waterflood/Water Drive</Typography>}
              size="small" 
              checked={well?.params?.oil?.useWaterflood || false} 
              onChange={() => handleChangeUseWaterflood(well?.params?.oil?.useWaterflood, list?.forecastListTab)}
              disabled={list?.forecastListTab === 1 || (list?.forecastListTab === 2 && well?.fullCycle) || !well?.production?.byFluid?.oil?.length > 0 || !well?.production?.byFluid?.water?.length > 0}
            />
          </FormGroup>
          <Tooltip title="Oil forecast will use the waterflood/water drive model">
            <Info fontSize="12px" style={{marginLeft: -10}} color={darkMode ? 'inherit' : 'primary'} />
          </Tooltip>
        </div>}

        {list.forecastListTab === 0 && ((options.fluid !== 'oil') || (options.fluid === 'oil' && !well?.params?.oil?.useWaterflood)) && 
        <div style={{display: 'flex', alignItems: 'center'}}>
          <FormGroup style={{marginTop: 5, marginLeft: 10}}>
            <FormControlLabel 
              control={<Switch />} 
              label={<Typography style={{fontSize: 14}}>Use Assisted Forecast</Typography>}
              size="small" 
              checked={options?.assistedForecast} 
              onChange={() => dispatch(toggleAssistedForecast())}
              disabled={!list.typeCurveId || list.typeCurveId?.length === 0 || !typeCurve || !typeCurve.params}
            />
          </FormGroup>
          <Tooltip title="Forecast will use selected type curve as a guide function">
            <Info fontSize="12px" style={{marginLeft: -10}} color={darkMode ? 'inherit' : 'primary'} />
          </Tooltip>
        </div>}

        <FormControl component="fieldset" style={{width: '100%', padding: '0 10px', marginTop: 10}}>
          <Typography>Units</Typography>
          <ToggleButtonGroup
            value={options?.units || "metric"}
            exclusive
            onChange={handleUnitsChange}
            size="small"
            style={{height: 28, width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 20}}
          >
            <ToggleButton value="metric" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
              <Typography style={{fontSize: 12}}>
                Metric
              </Typography>
            </ToggleButton>
            <ToggleButton value="imperial" size="small" style={{width: '50%'}} color={(darkMode ? "" : "primary")}>
              <Typography style={{fontSize: 12}}>
                Field
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <Divider />

        {options?.fluid === 'oil' && well?.params?.oil?.useWaterflood && (list?.forecastListTab === 0 || list?.forecastListTab === 2) ?
          <Accordion
            defaultExpanded={true}
            elevation={0}
            style={{backgroundColor: theme.palette?.elevationOne?.[darkMode ? 'dark' : 'light'], borderRadius: 0}}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography className={classes.heading} noWrap>Forecast Settings</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              {['ti', 'qi', 'di', 'b', 'maxFluidRate', 'cutoff'].map(param => {
                return (
                  <div key={param}>
                    <WaterfloodSetting well={well} param={param} darkMode={darkMode} forecastListTab={list?.forecastListTab}/>
                    <Divider style={{width: '100%'}}/>
                  </div>
                )
              })}
            </AccordionDetails>
          </Accordion>
          :
          <div>
            {list?.forecastListTab === 0 && 
            <Accordion 
              defaultExpanded={false}
              elevation={0}
              style={{backgroundColor: theme.palette?.elevationOne?.[darkMode ? 'dark' : 'light'], borderRadius: 0}}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography className={classes.heading} noWrap>Normalization</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <ForecastSetting forecastListTab={list?.forecastListTab} well={well} parameter={'normalization'} typeCurve={typeCurve} darkMode={darkMode} lockedParams={lockedParams} setLockedParams={setLockedParams}/>
                <Divider style={{width: '100%'}}/>
              </AccordionDetails>
            </Accordion>}

            <Accordion 
              defaultExpanded={false}
              elevation={0}
              style={{backgroundColor: theme.palette?.elevationOne?.[darkMode ? 'dark' : 'light'], borderRadius: 0}}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography className={classes.heading} noWrap>Segment 1</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                
                <div style={{width: '100%', display: 'flex', justifyContent: 'flex-end', paddingRight: 20}}>
                  <Button 
                    startIcon={well?.params?.[options?.fluid]?.plateau ? <Remove /> : <Add />} 
                    color={(darkMode ? "inherit" : "primary")}
                    variant="outlined" 
                    size="small" 
                    onClick={() => handleAddPlateau(list?.forecastListTab, (list?.forecastListTab === 0 ? well : list?.forecastListTab === 1 ? typeCurve : groupWell), options.fluid)}
                  >
                    {well?.params?.[options?.fluid]?.plateau ? 'Remove' : 'Add'} Plateau
                  </Button>
                </div>

                {['ti', 'q1i', 'di', 'b1', 'dlim'].map(param => {
                  return (
                    <div key={param}>
                      <ForecastSetting forecastListTab={list?.forecastListTab} well={well} parameter={param} darkMode={darkMode} lockedParams={lockedParams} setLockedParams={setLockedParams}/>
                      <Divider style={{width: '100%'}}/>
                    </div>
                  );
                })}
              </AccordionDetails>
            </Accordion>

            <Accordion 
              defaultExpanded={false}
              elevation={0}
              style={{backgroundColor: theme.palette?.elevationOne?.[darkMode ? 'dark' : 'light'], borderRadius: 0}}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography className={classes.heading} noWrap>Segment 2</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                {['b2', 'd2lim'].map(param => {
                  return (
                    <div key={param}>
                      <ForecastSetting forecastListTab={list?.forecastListTab} well={well} parameter={param} darkMode={darkMode} lockedParams={lockedParams} setLockedParams={setLockedParams}/>
                      <Divider style={{width: '100%'}}/>
                    </div>
                  );
                })}
              </AccordionDetails>
            </Accordion>

            <Accordion 
              defaultExpanded={false}
              elevation={0}
              style={{backgroundColor: theme.palette?.elevationOne?.[darkMode ? 'dark' : 'light'], borderRadius: 0}}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography className={classes.heading} noWrap>Segment 3</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                {['b3'].map(param => {
                  return (
                    <div key={param}>
                      <ForecastSetting forecastListTab={list?.forecastListTab} well={well} parameter={param} darkMode={darkMode} lockedParams={lockedParams} setLockedParams={setLockedParams}/>
                      <Divider style={{width: '100%'}}/>
                    </div>
                  );
                })}
              </AccordionDetails>
            </Accordion>

            <Accordion 
              defaultExpanded={false}
              elevation={0}
              style={{backgroundColor: theme.palette?.elevationOne?.[darkMode ? 'dark' : 'light'], borderRadius: 0}}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography className={classes.heading} noWrap>Forecast Cutoff</Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.details}>
                <ForecastSetting forecastListTab={list?.forecastListTab} well={well} parameter={'cutoff'} darkMode={darkMode} lockedParams={lockedParams} setLockedParams={setLockedParams}/>
                <Divider style={{width: '100%'}}/>
              </AccordionDetails>
            </Accordion>
          </div>
        
        }
      </>
      :
      <div className={classes.emptyForecast}>
        <Typography variant = "h6" color="textSecondary" style={{marginTop: 20}}>Forecast not available</Typography>
      </div>}
    </Container>
  );

  // FUNCTIONS
  function handleUnitsChange(e, newUnits) {
    newUnits && dispatch(setUnits({ units: newUnits }));
  };

  function handleChangeUseWaterflood(useWaterflood, forecastListTab) {
    if (useWaterflood) {
      // const newParams = {...well.params, oil: {...well.params.oil, useWaterflood: false}};
      // dispatch(updateWell({uwi: well.uwi, params: newParams}));
      if (forecastListTab === 0) {
        dispatch(getForecast(well));
      } else if (forecastListTab === 1) {
        dispatch(getTypeCurveForecast(well));
      } else if (forecastListTab === 2) {
        dispatch(getGroupWellForecast(well));
      };
    } else {
      let newParams = {...well.params};
      newParams.oil = {...newParams.oil, useWaterflood: true};
      if (!newParams.waterflood) {
        newParams.waterflood = {ti: 25, qi: 0.5, di: 0.1, b: 1, maxFluidRate: 500, cutoff: 1 };
      };
      if (forecastListTab === 0) {
        dispatch(getWaterfloodForecast({...well, params: newParams}));
      } else if (forecastListTab === 2) {
        dispatch(getWaterfloodForecastGW({...well, params: newParams}));
      };
    };
  };


  function handleRevertClick(well, tabItem) {
    try {
      if (tabItem === 0) {
        console.log('Reverting parameters for well ' + well?.uwi);
        
        let newParams = currentProject?.wells?.find(w => w.uwi === well.uwi)?.params;

        if (newParams) {
          if (newParams?.oil?.useWaterflood) { // if waterflood
            dispatch(getWaterfloodForecast({...well, params: newParams, lastModified: new Date().toLocaleString()})); // Generate waterflood forecast for the well
          } else {
            dispatch(getForecast({...well, params: newParams, lastModified: new Date().toLocaleString()})); // Generate multi segment forecast for the well
          };
        } else {
          // if local params not found, revert to global params
          dispatch(getParams({well: well, email: user.value.email}));
        };

      } else if (tabItem === 1) {
        console.log('Reverting parameters for type curve ' + well?.id);
        dispatch(getTypeCurveParams({well: well, email: user.value.email}));
      } else if (tabItem === 2) {
        console.log('Reverting parameters for group well ' + well?.id);
        dispatch(getGroupWellParams({well: well, email: user.value.email}));
      };
    } catch (error) {
      console.log('Could not reset parameters');
    };
  };
  
  function handleRefitClick(well, tabItem) {
    try {
      if (tabItem === 0) {
        console.log('Re-fitting well ' + well?.uwi);
        if (!well.local && !well.copy) {
          dispatch(updateWellStatus({uwi: well?.uwi, status: 'processing'}));
          sendProcessRequest([well?.uwi], true, well?.params, false);
        } else {
          dispatch(updateWellStatus({uwi: well.uwi, status: 'processing'}));
          sendProcessRequest([well?.uwi], true, well?.params, true);
        };
      } else if (tabItem === 1) {
        console.log('Autofitting type curve ' + well?.name);
        dispatch(getTypeCurveParams({well: well, email: user.value.email}));
      } else if (tabItem === 2) {
        console.log('Autofitting group well ' + well?.name);
        dispatch(getGroupWellParams({well: well, email: user.value.email}));
      };
    } catch (error) {
      console.log('Could not Autofit well');
      return;
    };
  };

  function sendProcessRequest(uwis, reFit, params, local) {
    try {
      let localParams = [];
      currentProject?.wells?.forEach(w => {
        let newWell = {...w};
        let wellParams = {...newWell.params};
        const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
        
        for (let fluid of wellFluids) {
          wellParams[fluid] = {...wellParams[fluid], outliers: []};
        };
        localParams.push({...newWell, params: wellParams});
      });
      if (socket && socket.readyState === 1) {
        socket.send(JSON.stringify({
          messageType: local ? 'localWell' : 'process', 
          payload: uwis || [], 
          userEmail: user?.value?.email, 
          params,
          localParams,
          reFit,
          assistedForecast: (options?.assistedForecast || false), 
          tcParams: typeCurve?.params
        }));
      };
    } catch (error) {
      console.log(error);
    };
  };

  function handleSaveClick(well, tabItem) {
    try {
      if (tabItem === 0) {
        console.log('Saving parameters for well ' + well.uwi);
        dispatch(saveParams({well: well, email: user.value.email})).then(res => {
          if (res?.payload?.success) {
            enqueueSnackbar(`Well ${well.uwi} saved successfully`, {variant: 'success'});
          };
        });
      } else if (tabItem === 1) {
        console.log('Saving parameters for type curve ' + well.name);
        dispatch(saveTypeCurveParams(well)).then(res => {
          if (res?.payload?.success) {
            enqueueSnackbar(`Type curve ${well.name} saved successfully`, {variant: 'success'});
          };
        });
      } else if (tabItem === 2) {
        console.log('Saving parameters for group well ' + well.name);
        dispatch(saveGroupWellParams(well)).then(res => {
          if (res?.payload?.success) {
            enqueueSnackbar(`Group well ${well.name} saved successfully`, {variant: 'success'});
          };
        });
      };
    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleAddPlateau(forecastListTab, well, fluid) {
    try {
      
      let newParams = {...well.params};
      if (newParams[fluid].plateau) {
        newParams[fluid] = {...newParams[fluid], plateau: false};
      } else {
        newParams[fluid] = {...newParams[fluid], plateau: true};
      };

      if (forecastListTab === 0) {
        dispatch(getForecast({...well, params: newParams}));
      } else if (forecastListTab === 1) {
        dispatch(getTypeCurveForecast({...well, params: newParams}));
      } else {
        dispatch(getGroupWellForecast({...well, params: newParams}));
      };

    } catch (error) {
      console.log(error);
    };
  };
};
